/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

export type DateRangeOption = 
  | 'current_month' 
  | 'last_month' 
  | 'last_3_months' 
  | 'last_6_months' 
  | 'all_time'
  | string; // Allows dynamic year strings like '2024', '2025', etc.

export interface DateRangeOptionItem {
  value: DateRangeOption;
  label: string;
}

/**
 * Converts a date range option to an ISO date string filter
 * @param range - The date range option selected
 * @param userCreatedAt - The user's account creation date
 * @returns ISO date string for the start of the range
 */
export const getDateRangeFilter = (range: DateRangeOption, userCreatedAt: string): string => {
  const now = new Date();
  
  // Check if it's a year-based range
  if (/^\d{4}$/.test(range)) {
    const year = parseInt(range);
    const userJoinDate = new Date(userCreatedAt);
    const userJoinYear = userJoinDate.getFullYear();
    
    // If it's the year they joined, use their actual join date
    if (year === userJoinYear) {
      return userCreatedAt;
    }
    
    // Otherwise, use January 1st of that year
    return new Date(year, 0, 1, 0, 0, 0, 0).toISOString();
  }
  
  switch (range) {
    case 'current_month':
      // First day of current month at midnight
      return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0).toISOString();
    
    case 'last_month':
      // First day of last month at midnight
      return new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0).toISOString();
    
    case 'last_3_months':
      // First day of 3 months ago at midnight
      return new Date(now.getFullYear(), now.getMonth() - 2, 1, 0, 0, 0, 0).toISOString();
    
    case 'last_6_months':
      // First day of 6 months ago at midnight
      return new Date(now.getFullYear(), now.getMonth() - 5, 1, 0, 0, 0, 0).toISOString();
    
    case 'all_time':
    default:
      // Use user's creation date for "all time"
      return userCreatedAt;
  }
};

/**
 * Gets the end date for a date range filter
 * @param range - The date range option selected
 * @param userCreatedAt - The user's account creation date
 * @returns ISO date string for the end of the range
 */
export const getDateRangeEndFilter = (range: DateRangeOption, userCreatedAt: string): string => {
  const now = new Date();
  
  // Check if it's a year-based range
  if (/^\d{4}$/.test(range)) {
    const year = parseInt(range);
    // End of December 31st of that year
    return new Date(year, 11, 31, 23, 59, 59, 999).toISOString();
  }
  
  switch (range) {
    case 'current_month':
      // End of current month (11:59:59.999 PM on last day)
      return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();
    
    case 'last_month':
      // End of last month (11:59:59.999 PM on last day)
      return new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999).toISOString();
    
    case 'last_3_months':
      // End of current month
      return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();
    
    case 'last_6_months':
      // End of current month
      return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();
    
    case 'all_time':
    default:
      // Current date/time
      return now.toISOString();
  }
};

/**
 * Gets a human-readable label for a date range option
 * @param range - The date range option
 * @param userCreatedAt - The user's account creation date
 * @returns Human-readable label
 */
export const getDateRangeLabel = (range: DateRangeOption, userCreatedAt: string): string => {
  const now = new Date();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Check if it's a year-based range
  if (/^\d{4}$/.test(range)) {
    const year = parseInt(range);
    const userJoinDate = new Date(userCreatedAt);
    const userJoinYear = userJoinDate.getFullYear();
    
    // If it's their join year, show "Since Month"
    if (year === userJoinYear) {
      const joinMonth = monthNames[userJoinDate.getMonth()];
      return `${year} (Since ${joinMonth})`;
    }
    
    // Otherwise just show the year
    return `${year}`;
  }
  
  switch (range) {
    case 'current_month':
      // e.g., "November 2024"
      return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
    
    case 'last_month':
      // e.g., "October 2024"
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return `${monthNames[lastMonth.getMonth()]} ${lastMonth.getFullYear()}`;
    
    case 'last_3_months':
      // e.g., "Sep-Nov 2024"
      const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      return `${monthNames[threeMonthsAgo.getMonth()]}-${monthNames[now.getMonth()]} ${now.getFullYear()}`;
    
    case 'last_6_months':
      // e.g., "Jun-Nov 2024"
      const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
      return `${monthNames[sixMonthsAgo.getMonth()]}-${monthNames[now.getMonth()]} ${now.getFullYear()}`;
    
    case 'all_time':
      return 'All Time';
    
    default:
      return 'Unknown';
  }
};

/**
 * Gets all available date range options for a user based on their account creation date
 * @param userCreatedAt - The user's account creation date (ISO string)
 * @returns Array of date range options with their labels
 */
export const getAvailableDateRangeOptions = (userCreatedAt: string): DateRangeOptionItem[] => {
  const options: DateRangeOptionItem[] = [];
  
  // Step 1: Add standard date ranges
  options.push({ value: 'current_month', label: getDateRangeLabel('current_month', userCreatedAt) });
  options.push({ value: 'last_month', label: getDateRangeLabel('last_month', userCreatedAt) });
  options.push({ value: 'last_3_months', label: getDateRangeLabel('last_3_months', userCreatedAt) });
  options.push({ value: 'last_6_months', label: getDateRangeLabel('last_6_months', userCreatedAt) });
  
  // Step 2: Add year-based ranges (from current year down to join year)
  const userJoinDate = new Date(userCreatedAt);
  const userJoinYear = userJoinDate.getFullYear();
  const currentYear = new Date().getFullYear();
  
  for (let year = currentYear; year >= userJoinYear; year--) {
    options.push({
      value: year.toString(),
      label: getDateRangeLabel(year.toString(), userCreatedAt)
    });
  }
  
  // Step 3: Add 'all_time' at the end
  options.push({ value: 'all_time', label: getDateRangeLabel('all_time', userCreatedAt) });
  
  return options;
};

/**
 * Gets the end date for a date range (useful for display purposes)
 * @param range - The date range option
 * @returns ISO date string for the end of the range (or current date)
 */
export const getDateRangeEnd = (range: DateRangeOption): string => {
  const now = new Date();
  
  switch (range) {
    case 'last_month':
      // Last day of last month
      return new Date(now.getFullYear(), now.getMonth(), 0).toISOString();
    default:
      // Current date for all other ranges
      return now.toISOString();
  }
};